import React, { useRef } from "react";
import { cn } from "./cn";
import { FieldLabel } from "./FieldLabel";
import { UploadIcon, CloseIcon } from "../../../assets/ui/icons";

/* Dashed drop zone for a single document — the trade licence on step 2. */
export const FileUpload = ({
  label = "Trade License",
  id = "file-upload",
  value = null,
  onChange = () => {},
  recommended = false,
  hint = "PDF, JPG, or PNG",
  accept = ".pdf,.jpg,.jpeg,.png",
  className = "",
}) => {
  const inputRef = useRef(null);

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        {recommended && (
          <span className="rounded-full bg-[var(--field-inset)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted)] font-sans">
            Recommended
          </span>
        )}
      </div>

      <div className="relative mt-1.5">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={cn(
            /* The shared drop-target treatment — see FileDropzone. */
            "flex w-full items-center gap-3 rounded-[13px] border-[1.5px] border-dashed",
            "border-[var(--dropzone-border)] bg-[var(--field-inset)] px-4 py-4 text-left transition-colors",
            "hover:border-[var(--coral)] hover:bg-[var(--coral-soft)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)] focus-visible:ring-offset-2"
          )}
        >
          <span
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--orange-soft)] text-[var(--orange)]"
          >
            <UploadIcon />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-semibold text-[var(--ink)] font-sans">
              {value ? value.name : `Click to upload ${String(label).toLowerCase()}`}
            </span>
            <span className="mt-0.5 block text-[12px] text-[var(--muted)] font-sans">
              {value ? "Click to replace" : hint}
            </span>
          </span>
        </button>

        {/* Outside the button — a button inside a button is invalid markup and
            the inner one would not receive its own clicks. */}
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              if (inputRef.current) inputRef.current.value = "";
            }}
            aria-label={`Remove ${value.name}`}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1.5 text-[var(--muted)] hover:text-[var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)]"
          >
            <CloseIcon size={14} />
          </button>
        )}

        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0] || null)}
        />
      </div>
    </div>
  );
};

export default FileUpload;
